import { api } from "./client";
import type { ChatAttachment, ChatJob, ChatMessage, DownloadUrl } from "@/types";

/** GET {Jobs}/staff/chat/jobs — the single feed for both chat sidebars and the
 *  nav badge (Staff Chat Brief 01 §3.2.2). Sorted server-side: active threads
 *  by recency, then no-conversation jobs alphabetically. */
export function listChatJobs(signal?: AbortSignal): Promise<ChatJob[]> {
  return api.get<ChatJob[]>("jobs", "staff/chat/jobs", { signal });
}

/** Messages predating attachments carry ""/null in `attachments`. */
type WireChatMessage = Omit<ChatMessage, "attachments"> & {
  attachments: ChatAttachment[] | "" | null;
};

function normalizeMessage(m: WireChatMessage): ChatMessage {
  return { ...m, attachments: Array.isArray(m.attachments) ? m.attachments : [] };
}

/** GET {Jobs}/staff/chat/{job_id}/messages — thread ascending. Server-side
 *  side effect: marks the whole job read for the calling staff user. */
export async function listMessages(jobId: number, signal?: AbortSignal): Promise<ChatMessage[]> {
  const msgs = await api.get<WireChatMessage[]>("jobs", `staff/chat/${jobId}/messages`, { signal });
  return msgs.map(normalizeMessage);
}

/** POST {Jobs}/staff/chat/{job_id}/messages — send as the signed-in staff
 *  user; the server fans out push (+ email once templated) to the job's active
 *  portal users. A send needs text OR ≥1 attachment id; every id must be the
 *  caller's own staged upload on this job, not yet attached to a message. */
export async function sendMessage(
  jobId: number,
  input: { body?: string; attachmentIds?: number[] },
): Promise<ChatMessage> {
  const msg = await api.post<WireChatMessage>("jobs", `staff/chat/${jobId}/messages`, {
    body: { body: input.body ?? "", attachment_ids: input.attachmentIds ?? [] },
  });
  return normalizeMessage(msg);
}

/** PATCH {Jobs}/staff/chat/{job_id}/messages/{message_id} — edit the text of
 *  one of the signed-in user's own staff messages (server-enforced: 403 on a
 *  colleague's or a client's row). Text only — attachments stay as sent; the
 *  body may be empty only if the message carries an attachment. Returns the
 *  row in the thread shape with `edited_at` stamped. Nothing is re-notified. */
export async function editMessage(
  jobId: number,
  messageId: number,
  body: string,
): Promise<ChatMessage> {
  const msg = await api.patch<WireChatMessage>("jobs", `staff/chat/${jobId}/messages/${messageId}`, {
    body: { body },
  });
  return normalizeMessage(msg);
}

/** DELETE {Jobs}/staff/chat/{job_id}/messages/{message_id} — soft-delete one of
 *  the signed-in user's own staff messages (same ownership rule as edit). The
 *  message vanishes for the client too on their next poll; its attachments go
 *  with it. No tombstone, no undo. */
export function deleteMessage(jobId: number, messageId: number): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>("jobs", `staff/chat/${jobId}/messages/${messageId}`);
}

/** POST {Jobs}/staff/chat/upload — multipart; stages one attachment for a later
 *  send on this job. Server enforces 20 MB max and image/* + application/pdf
 *  only, with human-readable messages — surface rejections verbatim. Abandoned
 *  staged uploads are harmless (no cleanup needed). */
export function uploadAttachment(jobId: number, file: File): Promise<ChatAttachment> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("job_id", String(jobId));
  return api.post<ChatAttachment>("jobs", "staff/chat/upload", { body: fd });
}

/** GET {Jobs}/staff/chat/attachment/{id}/download-url — 300 s presigned URL.
 *  Fetch at view/click time and use immediately; never cache or store it.
 *  Staged (not-yet-sent) uploads 404 by design. */
export function getAttachmentDownloadUrl(attachmentId: number): Promise<DownloadUrl> {
  return api.get<DownloadUrl>("jobs", `staff/chat/attachment/${attachmentId}/download-url`);
}

/** Same endpoint with thumb=true — a 1 h presigned GET for a PDF's first-page
 *  preview. 404 "No preview" until one has been made; ask only when the
 *  attachment carries `has_thumb`. */
export function getAttachmentPreviewUrl(attachmentId: number): Promise<DownloadUrl> {
  return api.get<DownloadUrl>("jobs", `staff/chat/attachment/${attachmentId}/download-url`, {
    query: { thumb: true },
  });
}

/** POST {Jobs}/staff/chat/attachment/{id}/preview — have the server render a
 *  sent PDF's first-page preview (Modal app arkitectly-previews). New uploads
 *  get theirs at upload time; this backfills PDFs sent before that existed.
 *  Sets `has_thumb` on the message's attachment entry, so the phone apps pick
 *  it up on their next poll too. `has_thumb: false` + `reason` when it can't
 *  (encrypted PDF, renderer down) — never an error for that. */
export function requestAttachmentPreview(
  attachmentId: number,
): Promise<{ attachment_id: number; has_thumb: boolean; reason: string }> {
  return api.post("jobs", `staff/chat/attachment/${attachmentId}/preview`);
}
